import { Breadcrumbs } from "@/components/Breadcrumbs/breadcrumbs";
import { PermissionTooltip } from "@/components/PermissionGate";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useReportPageReady } from "@/hooks/useReportPageReady";
import { Textarea } from "@/components/Textarea/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePermissions } from "@/contexts/usePermissions";
import { getApiErrorMessage } from "@/lib/errors";
import { showErrorToast, showSuccessToast } from "@/lib/toast";
import { useOrganizationSettingsPaths } from "@/lib/organizationSettingsPaths";
import { Edit2, Key, Loader2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  useDeleteSecret,
  useSecret,
  useSetSecretKey,
  useDeleteSecretKey,
  useUpdateSecretName,
} from "@/hooks/useSecrets";

interface SecretDetailProps {
  organizationId: string;
}

export function SecretDetail({ organizationId }: SecretDetailProps) {
  const navigate = useNavigate();
  const { secretId } = useParams<{ secretId: string }>();
  const settingsPaths = useOrganizationSettingsPaths(organizationId);
  const { canAct, isLoading: permissionsLoading } = usePermissions();

  const { data: secret, isLoading, error } = useSecret(organizationId, "DOMAIN_TYPE_ORGANIZATION", secretId || "");
  usePageTitle(["Secrets", secret?.metadata?.name]);

  useReportPageReady(!isLoading && !!secretId, {
    failed: !!error,
  });

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingKeyName, setEditingKeyName] = useState("");
  const [editingValue, setEditingValue] = useState("");
  const [isAddingKey, setIsAddingKey] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [editingSecretName, setEditingSecretName] = useState(false);
  const [editingSecretNameValue, setEditingSecretNameValue] = useState("");

  const setSecretKeyMutation = useSetSecretKey(organizationId, "DOMAIN_TYPE_ORGANIZATION", secretId || "");
  const deleteSecretKeyMutation = useDeleteSecretKey(organizationId, "DOMAIN_TYPE_ORGANIZATION", secretId || "");
  const updateSecretNameMutation = useUpdateSecretName(organizationId, "DOMAIN_TYPE_ORGANIZATION", secretId || "");
  const deleteSecretMutation = useDeleteSecret(organizationId, "DOMAIN_TYPE_ORGANIZATION");
  const canUpdateSecrets = canAct("secrets", "update");
  const canDeleteSecrets = canAct("secrets", "delete");

  const handleSaveEdit = async () => {
    if (!canUpdateSecrets) return;
    if (!secret || !editingKey || !editingValue.trim()) return;
    const newName = editingKeyName.trim();
    if (!newName) {
      showErrorToast("Key name is required");
      return;
    }
    const secretData = secret.spec?.local?.data || {};
    const otherKeys = Object.keys(secretData).filter((k) => k !== editingKey);
    if (newName !== editingKey && otherKeys.includes(newName)) {
      showErrorToast("Key already exists");
      return;
    }
    try {
      if (newName !== editingKey) {
        await deleteSecretKeyMutation.mutateAsync(editingKey);
        await setSecretKeyMutation.mutateAsync({ keyName: newName, value: editingValue.trim() });
      } else {
        await setSecretKeyMutation.mutateAsync({ keyName: newName, value: editingValue.trim() });
      }
      showSuccessToast(newName !== editingKey ? "Key updated" : "Value updated");
      setEditingKey(null);
      setEditingKeyName("");
      setEditingValue("");
    } catch (err) {
      showErrorToast(`Failed to update: ${getApiErrorMessage(err)}`);
    }
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditingKeyName("");
    setEditingValue("");
  };

  const handleStartEditSecretName = () => {
    if (!canUpdateSecrets) return;
    setEditingSecretNameValue(secret?.metadata?.name || "");
    setEditingSecretName(true);
  };

  const handleSaveSecretName = async () => {
    if (!canUpdateSecrets) return;
    const name = editingSecretNameValue.trim();
    if (!name) {
      showErrorToast("Secret name is required");
      return;
    }
    if (name === secret?.metadata?.name) {
      setEditingSecretName(false);
      setEditingSecretNameValue("");
      return;
    }
    try {
      await updateSecretNameMutation.mutateAsync(name);
      showSuccessToast("Secret name updated");
      setEditingSecretName(false);
      setEditingSecretNameValue("");
    } catch (err) {
      showErrorToast(`Failed to update name: ${getApiErrorMessage(err)}`);
    }
  };

  const handleCancelEditSecretName = () => {
    setEditingSecretName(false);
    setEditingSecretNameValue("");
  };

  const handleAddKey = async () => {
    if (!canUpdateSecrets) return;
    if (!secret || !newKey.trim() || !newValue.trim()) {
      showErrorToast("Key and value are required");
      return;
    }
    const secretData = secret.spec?.local?.data || {};
    if (Object.keys(secretData).includes(newKey.trim())) {
      showErrorToast("Key already exists");
      return;
    }
    try {
      await setSecretKeyMutation.mutateAsync({
        keyName: newKey.trim(),
        value: newValue.trim(),
      });
      showSuccessToast("Key added");
      setIsAddingKey(false);
      setNewKey("");
      setNewValue("");
    } catch (err) {
      showErrorToast(`Failed to add key: ${getApiErrorMessage(err)}`);
    }
  };

  const handleCancelAdd = () => {
    setIsAddingKey(false);
    setNewKey("");
    setNewValue("");
  };

  const handleRemoveKey = async (keyToRemove: string) => {
    if (!canUpdateSecrets) return;
    if (!secret) return;
    const secretData = secret.spec?.local?.data || {};
    const remainingCount = Object.keys(secretData).filter((k) => k !== keyToRemove).length;
    if (remainingCount === 0) {
      showErrorToast("Secret must have at least one key");
      return;
    }
    try {
      await deleteSecretKeyMutation.mutateAsync(keyToRemove);
      showSuccessToast("Key removed");
      if (editingKey === keyToRemove) {
        setEditingKey(null);
        setEditingValue("");
      }
    } catch (err) {
      showErrorToast(`Failed to remove key: ${getApiErrorMessage(err)}`);
    }
  };

  const handleDelete = async () => {
    if (!canDeleteSecrets) return;
    if (!secret) return;
    if (
      !confirm(`Are you sure you want to delete the secret "${secret.metadata?.name}"? This action cannot be undone.`)
    ) {
      return;
    }
    try {
      await deleteSecretMutation.mutateAsync(secret.metadata?.id ?? "");
      showSuccessToast("Secret deleted successfully");
      navigate(settingsPaths.secrets);
    } catch (err) {
      showErrorToast(`Failed to delete secret: ${getApiErrorMessage(err)}`);
    }
  };

  const isUpdating =
    setSecretKeyMutation.isPending || deleteSecretKeyMutation.isPending || updateSecretNameMutation.isPending;
  const handleBackToSecrets = () => navigate(settingsPaths.secrets);

  if (isLoading || !secretId) {
    return (
      <div className="space-y-6 pt-6">
        <div className="mb-4">
          <Breadcrumbs
            items={[
              { label: "Secrets", onClick: handleBackToSecrets },
              { label: "Secret", current: true },
            ]}
            showDivider={false}
          />
        </div>
        <div className="flex justify-center items-center h-32">
          <p className="text-gray-500 dark:text-gray-400">{!secretId ? "Secret not found" : "Loading..."}</p>
        </div>
      </div>
    );
  }

  if (error || !secret) {
    return (
      <div className="space-y-6 pt-6">
        <div className="mb-4">
          <Breadcrumbs
            items={[
              { label: "Secrets", onClick: handleBackToSecrets },
              { label: "Secret", current: true },
            ]}
            showDivider={false}
          />
        </div>
        <div className="bg-white border border-red-300 text-red-500 px-4 py-2 rounded dark:border-red-800 dark:bg-red-900/20">
          <p>{error instanceof Error ? error.message : "Secret not found or failed to load."}</p>
        </div>
      </div>
    );
  }

  const secretData = secret.spec?.local?.data || {};
  const keys = Object.keys(secretData);

  return (
    <div className="space-y-6 pt-6">
      <div className="mb-4">
        <Breadcrumbs
          items={[
            { label: "Secrets", onClick: handleBackToSecrets },
            { label: secret.metadata?.name || "Unnamed Secret", current: true },
          ]}
          showDivider={false}
        />
      </div>

      <div className="bg-slate-50 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-800 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Key className="w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0" />
            {editingSecretName ? (
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Input
                  type="text"
                  value={editingSecretNameValue}
                  onChange={(e) => setEditingSecretNameValue(e.target.value)}
                  placeholder="Secret name"
                  className="text-lg font-semibold max-w-sm"
                  data-testid="secret-detail-edit-name-input"
                  autoFocus
                />
                <Button
                  size="sm"
                  onClick={handleSaveSecretName}
                  disabled={!editingSecretNameValue.trim() || isUpdating}
                  data-testid="secret-detail-edit-name-save"
                >
                  {isUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleCancelEditSecretName}>
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 truncate">
                  {secret.metadata?.name || "Unnamed Secret"}
                </h2>
                <PermissionTooltip
                  allowed={canUpdateSecrets || permissionsLoading}
                  message="You don't have permission to update secrets."
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleStartEditSecretName}
                    disabled={!canUpdateSecrets}
                    className="shrink-0 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    title="Edit secret name"
                    data-testid="secret-detail-edit-name"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </PermissionTooltip>
              </>
            )}
          </div>
          <PermissionTooltip
            allowed={canDeleteSecrets || permissionsLoading}
            message="You don't have permission to delete secrets."
          >
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              disabled={deleteSecretMutation.isPending || !canDeleteSecrets}
              className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 shrink-0"
              title="Delete secret"
              data-testid="secret-detail-delete"
            >
              {deleteSecretMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              <span className="ml-1">Delete secret</span>
            </Button>
          </PermissionTooltip>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-800 overflow-hidden">
          <div className="p-6">
            <h3 className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-3">Key-value pairs</h3>
            <div className="space-y-2">
              {keys.map((keyName) => {
                const isEditing = editingKey === keyName;
                return (
                  <div
                    key={keyName}
                    className={
                      isEditing
                        ? "flex gap-2 items-start rounded-md border border-gray-200 dark:border-gray-600 p-4 bg-gray-50/50 dark:bg-gray-800/50 space-y-2"
                        : "flex items-center gap-2 text-sm font-mono bg-gray-50 dark:bg-gray-800 rounded px-3 py-2"
                    }
                  >
                    {isEditing ? (
                      <>
                        <div className="flex-1 min-w-0 space-y-2">
                          <Label className="text-xs text-gray-500 dark:text-gray-400 font-normal">Key</Label>
                          <Input
                            type="text"
                            value={editingKeyName}
                            onChange={(e) => setEditingKeyName(e.target.value)}
                            placeholder="Key name"
                            className="font-mono text-sm"
                            data-testid="secret-detail-edit-key-name"
                          />
                          <Label className="text-xs text-gray-500 dark:text-gray-400 font-normal block mt-2">
                            Value
                          </Label>
                          <Textarea
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            placeholder="Value"
                            rows={8}
                            className="font-mono text-sm resize-y bg-white dark:bg-gray-900 ph-no-capture"
                            autoFocus
                            data-testid="secret-detail-edit-value"
                          />
                          <div className="flex gap-2 mt-2">
                            <Button
                              size="sm"
                              onClick={handleSaveEdit}
                              disabled={!editingKeyName.trim() || !editingValue.trim() || isUpdating}
                              data-testid="secret-detail-edit-save"
                            >
                              {isUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-gray-700 dark:text-gray-200 shrink-0 w-32 truncate" title={keyName}>
                          {keyName}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">•••</span>
                        <PermissionTooltip
                          allowed={canUpdateSecrets || permissionsLoading}
                          message="You don't have permission to update secrets."
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (!canUpdateSecrets) return;
                              setEditingKey(keyName);
                              setEditingKeyName(keyName);
                              setEditingValue("");
                            }}
                            disabled={!canUpdateSecrets}
                            className="shrink-0 text-gray-600 dark:text-gray-300"
                            title="Edit value"
                            data-testid="secret-detail-edit-key"
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                        </PermissionTooltip>
                        {keys.length > 1 && (
                          <PermissionTooltip
                            allowed={canUpdateSecrets || permissionsLoading}
                            message="You don't have permission to update secrets."
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveKey(keyName)}
                              disabled={isUpdating || !canUpdateSecrets}
                              className="shrink-0 text-red-600 hover:text-red-700 dark:text-red-400"
                              title="Remove key"
                              data-testid="secret-detail-remove-key"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </PermissionTooltip>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
              {isAddingKey && (
                <div className="flex gap-2 items-start rounded-md border border-gray-200 dark:border-gray-600 border-dashed p-4 bg-gray-50/50 dark:bg-gray-800/50">
                  <div className="flex-1 min-w-0 space-y-2">
                    <Label className="text-xs text-gray-500 dark:text-gray-400 font-normal">Key</Label>
                    <Input
                      type="text"
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                      placeholder="Key name"
                      className="font-mono text-sm"
                      data-testid="secret-detail-add-key-name"
                    />
                    <Label className="text-xs text-gray-500 dark:text-gray-400 font-normal block mt-2">Value</Label>
                    <Textarea
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      placeholder="Value"
                      rows={8}
                      className="font-mono text-sm resize-y bg-white dark:bg-gray-900 ph-no-capture"
                      data-testid="secret-detail-add-value"
                    />
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm"
                        onClick={handleAddKey}
                        disabled={!newKey.trim() || !newValue.trim() || isUpdating}
                        data-testid="secret-detail-add-save"
                      >
                        {isUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={handleCancelAdd}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {!isAddingKey && (
              <PermissionTooltip
                allowed={canUpdateSecrets || permissionsLoading}
                message="You don't have permission to update secrets."
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!canUpdateSecrets) return;
                    setIsAddingKey(true);
                    setNewKey("");
                    setNewValue("");
                  }}
                  disabled={!canUpdateSecrets}
                  className="mt-3 text-xs"
                  data-testid="secret-detail-add-key"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add key
                </Button>
              </PermissionTooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
